import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiTrash2, FiChevronRight, FiChevronLeft, FiChevronDown, FiLoader, FiCheck,
  FiPlay, FiFileText, FiUser, FiClipboard, FiDownload,
  FiEdit2, FiX, FiBook, FiRefreshCw, FiUpload, FiUsers,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import { isGuideSeen, markGuideSeen } from '../utils/onboarding';
import { downloadAsDocx } from '../utils/docx';
import FeedbackButtons from '../components/AgentShared/FeedbackButtons';
import './GrantAgent.css';
import './PaperAgent.css';

const PA_STAGE_HINTS = {
  collection_input: "Select or confirm the source collection for your paper.",
  outline: "Generating an outline from your papers. This takes ~30 seconds.",
  author_profile: "Add your author details so the draft matches your voice.",
  drafting: "Your draft is being generated with real citations. Once done, it advances to Panel Review automatically.",
  panel_review: "Six reviewers are reading your draft. Accept or decline each issue, then click Iterate Draft to auto-fix. Save and re-run the review to keep improving.",
  done: "Your draft is ready. Download it, or switch between Draft / Panel Review tabs to keep iterating.",
};

const STAGES = [
  { key: 'collection_input', label: 'Collection',     type: 'human' },
  { key: 'outline',          label: 'Outline',        type: 'auto'  },
  { key: 'author_profile',   label: 'Author Profile', type: 'human' },
  { key: 'drafting',         label: 'Draft',          type: 'auto', loop: 'start' },
  { key: 'panel_review',     label: 'Panel Review',   type: 'auto', loop: 'end'   },
  { key: 'done',             label: 'Done',           type: null    },
];

// Post-done sub-pipeline keys reusing the existing DoneStage tabs.
const REVIEW_KEYS = ['review_draft', 'review_panel', 'review_results'];
const REVIEW_KEY_TO_TAB = {
  review_draft:   'draft',
  review_panel:   'review',
  review_results: 'results',
};

const STAGE_ICONS = {
  collection_input: <FiBook />,
  outline:          <FiFileText />,
  author_profile:   <FiUser />,
  drafting:         <FiClipboard />,
  panel_review:     <FiUsers />,
  done:             <FiCheck />,
  review_draft:     <FiClipboard />,
  review_panel:     <FiUsers />,
  review_results:   <FiFileText />,
};

const PAPER_TYPES = [
  { value: 'journal',    label: 'Journal Article' },
  { value: 'conference', label: 'Conference Paper' },
  { value: 'review',     label: 'Review / Survey' },
];

const SEVERITY_META = {
  high:   { label: 'High',   cls: 'ga-severity--high'   },
  medium: { label: 'Medium', cls: 'ga-severity--medium' },
  low:    { label: 'Low',    cls: 'ga-severity--low'    },
};

// ── Diff helpers (GitHub-style unified diff) ──────────────────────────────────

function lcsOps(aArr, bArr) {
  const m = aArr.length, n = bArr.length;
  if (m * n > 120000) return [...aArr.map(s => ({ t: 'del', s })), ...bArr.map(s => ({ t: 'add', s }))];
  const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = aArr[i-1] === bArr[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const ops = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aArr[i-1] === bArr[j-1]) { ops.unshift({ t: 'same', s: aArr[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { ops.unshift({ t: 'add', s: bArr[j-1] }); j--; }
    else { ops.unshift({ t: 'del', s: aArr[i-1] }); i--; }
  }
  return ops;
}

function wordNodes(ops, side) {
  return ops
    .filter(o => o.t !== (side === 'del' ? 'add' : 'del'))
    .map((o, i) =>
      o.t === side
        ? <mark key={i} className={`ga-diff-word-${side}`}>{o.s}</mark>
        : <span key={i}>{o.s}</span>
    );
}

function UnifiedDiff({ original, revised }) {
  const lineOps = lcsOps(original.split('\n'), revised.split('\n'));
  const rows = [];
  let k = 0;
  while (k < lineOps.length) {
    if (lineOps[k].t === 'same') { rows.push({ t: 'same', s: lineOps[k].s }); k++; }
    else {
      const dels = [], adds = [];
      while (k < lineOps.length && lineOps[k].t === 'del') { dels.push(lineOps[k].s); k++; }
      while (k < lineOps.length && lineOps[k].t === 'add') { adds.push(lineOps[k].s); k++; }
      const pairs = Math.min(dels.length, adds.length);
      for (let p = 0; p < pairs; p++) {
        const wops = lcsOps(dels[p].split(/(\s+)/), adds[p].split(/(\s+)/));
        rows.push({ t: 'del', rich: wordNodes(wops, 'del') });
        rows.push({ t: 'add', rich: wordNodes(wops, 'add') });
      }
      for (let p = pairs; p < dels.length; p++) rows.push({ t: 'del', s: dels[p] });
      for (let p = pairs; p < adds.length; p++) rows.push({ t: 'add', s: adds[p] });
    }
  }
  return (
    <div className="ga-diff-unified">
      {rows.map((r, idx) => (
        <div key={idx} className={`ga-diff-line ga-diff-line--${r.t}`}>
          <span className="ga-diff-sign">{r.t === 'del' ? '-' : r.t === 'add' ? '+' : ' '}</span>
          <span className="ga-diff-content">{r.rich ?? r.s}</span>
        </div>
      ))}
    </div>
  );
}

function stageIndex(key) {
  return STAGES.findIndex(s => s.key === key);
}

function stageState(draft, stageKey) {
  const current = stageIndex(draft.current_stage);
  const target  = stageIndex(stageKey);
  if (target < current) return 'done';
  if (target === current) return draft.stage_status === 'done' ? 'done' : 'active';
  return 'pending';
}

// ── Stage stepper ─────────────────────────────────────────────────────────────

function StageStepper({ draft, viewStage, onStageClick }) {
  const isDone = draft.current_stage === 'done';
  // Once the draft is done, replace Draft/Panel Review with the post-done
  // sub-pipeline (Draft ↔ Panel Review → Results) so navigation stays inside DoneStage.
  const setupStages = isDone
    ? STAGES.filter(s => !['drafting', 'panel_review', 'done'].includes(s.key))
    : STAGES.filter(s => s.key !== 'done');

  const reviewSteps = [
    { key: 'review_draft',   label: 'Draft',        loop: 'start' },
    { key: 'review_panel',   label: 'Panel Review', loop: 'end'   },
    { key: 'review_results', label: 'Results' },
  ];

  return (
    <div className="ga-stepper">
      {setupStages.map((s, i) => {
        const state = stageState(draft, s.key);
        const isRunning = state === 'active' && draft.stage_status === 'running';
        const isViewing = viewStage === s.key || (!viewStage && !isDone && s.key === draft.current_stage);
        const isClickable = state === 'done' || s.key === draft.current_stage;
        const showConnector = i < setupStages.length - 1 || isDone;
        const loopClass = s.loop ? ` ga-step--loop-${s.loop}` : '';
        const connectorIsLoop = s.loop === 'start';
        return (
          <React.Fragment key={s.key}>
            <div
              className={`ga-step ga-step--${state}${isRunning ? ' ga-step--running' : ''}${isViewing ? ' ga-step--viewing' : ''}${loopClass}`}
              onClick={isClickable ? () => onStageClick(viewStage === s.key ? null : s.key) : undefined}
              style={isClickable ? { cursor: 'pointer' } : {}}
            >
              <div className="ga-step-icon">
                {state === 'done' ? <FiCheck /> : isRunning ? <FiLoader className="ga-spin" /> : STAGE_ICONS[s.key]}
              </div>
              <span className="ga-step-label">{s.label}</span>
              {s.type && <span className="ga-step-type">{s.type === 'auto' ? 'auto' : 'you'}</span>}
            </div>
            {showConnector && (
              <div className={`ga-step-connector${state === 'done' ? ' ga-step-connector--done' : ''}${connectorIsLoop ? ' ga-step-connector--loop' : ''}`}>
                {connectorIsLoop && (
                  <span className="ga-loop-arrow" title="Iterate: review feedback rewrites the draft">
                    ⟲ iterate
                  </span>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {isDone && reviewSteps.map((s, i) => {
        const isViewing =
          viewStage === s.key ||
          (viewStage == null && s.key === 'review_results');
        const loopClass = s.loop ? ` ga-step--loop-${s.loop}` : '';
        return (
          <React.Fragment key={s.key}>
            <div
              className={`ga-step ga-step--done ga-step--review${loopClass}${isViewing ? ' ga-step--viewing' : ''}`}
              onClick={() => onStageClick(s.key)}
              style={{ cursor: 'pointer' }}
            >
              <div className="ga-step-icon">{STAGE_ICONS[s.key]}</div>
              <span className="ga-step-label">{s.label}</span>
              <span className="ga-step-type">
                {s.key === 'review_draft' ? 'edit' : s.key === 'review_panel' ? 'critique' : 'outputs'}
              </span>
            </div>
            {i < reviewSteps.length - 1 && (
              <div className={`ga-step-connector ga-step-connector--done${s.loop === 'start' ? ' ga-step-connector--loop' : ''}`}>
                {s.loop === 'start' && (
                  <span className="ga-loop-arrow" title="Iterate: review feedback rewrites the draft">
                    ⟲ iterate
                  </span>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Format panel review report ────────────────────────────────────────────────

function formatPanelReviewReport(pr, feedbackState, title) {
  if (!pr) return '';
  const syn = pr.synthesis || {};
  const lines = [
    `# Peer Review Report: ${title}`,
    `*Generated: ${pr.run_at ? new Date(pr.run_at).toLocaleString() : 'unknown'}*`,
    `*Venue type: ${pr.venue_type || 'Academic paper'}*`,
    '',
    '---',
    '',
    `## Editorial Decision: ${(syn.decision || '').replace(/_/g, ' ').toUpperCase()}`,
    `**Overall score:** ${syn.overall_score ?? '—'}/10`,
    '',
    syn.editorial_summary || '',
    '',
    '## Panel Strengths',
  ];
  (syn.top_strengths || []).forEach((s, i) => {
    const fb = feedbackState[`__synthesis__::top_strengths::${i}`]
      || pr.synthesis?.top_strengths_feedback?.[i];
    const tag = fb?.verdict === 'agree' ? ' ✓' : fb?.verdict === 'disagree' ? ' ✗' : '';
    lines.push(`- ${s}${tag}`);
    if (fb?.comment) lines.push(`  *Comment: ${fb.comment}*`);
  });
  lines.push('', '## Priority Issues');
  (syn.ranked_issues || []).forEach((iss, i) => {
    const fb = feedbackState[`__synthesis__::ranked_issues::${i}`] || iss.feedback;
    const tag = fb?.verdict === 'agree' ? ' ✓' : fb?.verdict === 'disagree' ? ' ✗' : '';
    lines.push(`### [${(iss.severity || '').toUpperCase()}] ${iss.section}: ${iss.issue}${tag}`);
    lines.push(`**Fix:** ${iss.fix}`);
    if (fb?.comment) lines.push(`*Disagree reason: ${fb.comment}*`);
    lines.push('');
  });
  lines.push('', '---', '', '## Individual Reviewer Reports');
  (pr.reviewers || []).forEach(rv => {
    lines.push(`### ${rv.name} — Score: ${rv.score ?? '?'}/10`);
    lines.push(rv.summary || '');
    lines.push('**Strengths:**');
    (rv.strengths || []).forEach(s => lines.push(`- ${s}`));
    lines.push('**Issues:**');
    (rv.issues || []).forEach((iss, i) => {
      const fb = feedbackState[`${rv.id}::issues::${i}`] || iss.feedback;
      const tag = fb?.verdict === 'agree' ? ' ✓' : fb?.verdict === 'disagree' ? ' ✗' : '';
      lines.push(`- [${(iss.severity || '').toUpperCase()}] ${iss.section}: ${iss.issue}${tag}`);
      lines.push(`  Fix: ${iss.fix}`);
    });
    lines.push('');
  });
  return lines.join('\n');
}

// ── Done stage ────────────────────────────────────────────────────────────────

export function DoneStage({
  draft,
  collections,
  onRefresh,
  user,
  tab: tabProp,
  onTabChange,
  onAdvance = null,
}) {
  // `tab` may be controlled by the parent stepper. Allowed values: draft | review | results.
  const [internalTab, setInternalTab] = useState('draft');
  const tab    = tabProp ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;
  const [showRaw, setShowRaw] = useState(false);
  const [editingSection, setEditing] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showRequestReview, setShowRequestReview] = useState(false);
  const [sectionAssignments, setSectionAssignments] = useState(draft.section_assignments || {});
  const [assigningSection, setAssigningSection] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const canPickTeam = !draft.collection_id && draft.user_id === user?.id;

  useEffect(() => {
    fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/team-members`), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setTeamMembers)
      .catch(() => setTeamMembers([]));
  }, [draft.id]);

  useEffect(() => {
    if (!canPickTeam) return;
    fetch(getApiEndpoint('/api/teams'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setUserTeams)
      .catch(() => setUserTeams([]));
  }, [canPickTeam]);

  const attachTeam = async (teamId) => {
    setShowTeamPicker(false);
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/team`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_id: teamId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to attach team');
      if (onRefresh) onRefresh(draft.id);
    } catch (e) { alert(e.message); }
  };
  const currentTeamName = () => {
    if (draft.team_id) {
      const t = userTeams.find(x => x.id === draft.team_id);
      return t ? t.name : null;
    }
    return null;
  };

  useEffect(() => {
    setSectionAssignments(draft.section_assignments || {});
  }, [draft.section_assignments]);

  const requestReview = async (reviewerUserId) => {
    try {
      await fetch(getApiEndpoint('/api/grant-proposals/peer-reviews'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewer_user_id: reviewerUserId, draft_id: draft.id }),
      });
      setShowRequestReview(false);
      alert('Review requested!');
    } catch (_) {}
  };

  const assignSection = async (sectionName, userId) => {
    const next = { ...sectionAssignments };
    if (userId) next[sectionName] = userId;
    else delete next[sectionName];
    setSectionAssignments(next);
    setAssigningSection(null);
    try {
      await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/section-assignments`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments: next }),
      });
      if (onRefresh) onRefresh(draft.id);
    } catch (_) { /* non-fatal */ }
  };

  const teamMemberName = (uid) => {
    const m = teamMembers.find(x => x.user_id === uid);
    return m ? (m.display_name || m.username) : `User ${uid}`;
  };
  const editsDraftKey = `pa_edits_${draft.id}`;
  const [edits, setEdits] = useState(() => {
    try { return JSON.parse(localStorage.getItem(editsDraftKey) || '{}'); } catch { return {}; }
  });
  const [expandedSections, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [reformatting, setReformatting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const [runningPanel, setRunningPanel] = useState(false);
  const [expandedReviewers, setExpandedReviewers] = useState({});
  const [reviewFeedback, setReviewFeedback] = useState({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const [iterating, setIterating] = useState(false);
  const [revisions, setRevisions] = useState(null);
  const [revisionDrafts, setRevisionDrafts] = useState({});
  const [revisionStatus, setRevisionStatus] = useState({});
  const [collapsedRevisions, setCollapsedRevisions] = useState({});

  useEffect(() => {
    try { localStorage.setItem(editsDraftKey, JSON.stringify(edits)); } catch {}
  }, [edits, editsDraftKey]);

  const hasUnsaved = Object.keys(edits).length > 0;
  const allSections = draft.paper_sections || {};
  const suggestedRefs = allSections._suggested_references || [];
  const citationProvenance = allSections._citation_provenance || {};
  const sections = Object.fromEntries(
    Object.entries(allSections).filter(([k]) => !k.startsWith('_'))
  );
  const collectionName = (collections || []).find(c => c.id === draft.collection_id)?.name
    || `Collection #${draft.collection_id}`;

  const assembledDraft = Object.entries({ ...sections, ...edits })
    .map(([name, body]) => `## ${name}\n\n${body}`)
    .join('\n\n');

  const saveSections = async () => {
    if (Object.keys(edits).length === 0) return true;
    setSaving(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/sections`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: edits }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setEdits({});
      try { localStorage.removeItem(editsDraftKey); } catch {}
      setSavedAt(new Date());
      if (onRefresh) onRefresh(draft.id);
      return true;
    } catch (e) {
      alert(e.message);
      return false;
    } finally { setSaving(false); }
  };

  const rerunDraft = async () => {
    if (!window.confirm('Regenerate the full draft? Unsaved edits will be lost.')) return;
    setRerunning(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/reset`), {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Re-run failed');
      if (onRefresh) onRefresh(draft.id);
    } catch (e) { alert(e.message); }
    finally { setRerunning(false); }
  };

  const reformatDraft = async () => {
    if (!draft.paper_sections) return;
    setReformatting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/reformat`), {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Reformat failed');
      if (onRefresh) onRefresh(draft.id);
    } catch (e) { alert(e.message); }
    finally { setReformatting(false); }
  };

  const runPanelReview = async () => {
    // Flush accepted-but-unsaved revisions first so the next review reads
    // the latest draft, not the stale DB copy.
    const ok = await saveSections();
    if (!ok) return;
    setRunningPanel(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/panel-review`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Review failed');
      if (onRefresh) onRefresh(draft.id);
      setExpandedReviewers({});
      // Clear local feedback overlay — index keys can shift across re-runs;
      // server-side feedback (carried forward) is the source of truth.
      setReviewFeedback({});
      setRevisions(null); setRevisionDrafts({}); setRevisionStatus({});
    } catch (e) { alert(e.message); }
    finally { setRunningPanel(false); }
  };

  // When the workflow lands on the panel_review stage and no review exists yet,
  // the backend already kicked off the review thread. Keep polling until done.
  useEffect(() => {
    const stillRunning =
      draft.current_stage === 'panel_review' &&
      (draft.stage_status === 'running' || (draft.stage_status === 'pending' && !draft.panel_review));
    if (stillRunning) {
      const id = setInterval(() => onRefresh && onRefresh(draft.id), 4000);
      return () => clearInterval(id);
    }
  }, [draft.current_stage, draft.stage_status, draft.id, draft.panel_review, onRefresh]);

  const getReviewFeedback = (reviewerId, itemType, itemIndex) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    if (Object.prototype.hasOwnProperty.call(reviewFeedback, key)) return reviewFeedback[key];
    const pr = draft.panel_review;
    if (!pr) return null;
    if (reviewerId === '__synthesis__') {
      if (itemType === 'top_strengths') return pr.synthesis?.top_strengths_feedback?.[itemIndex] || null;
      return pr.synthesis?.[itemType]?.[itemIndex]?.feedback || null;
    }
    const rv = (pr.reviewers || []).find(r => r.id === reviewerId);
    return (rv?.[itemType] || [])[itemIndex]?.feedback || null;
  };

  const saveReviewFeedback = (reviewerId, itemType, itemIndex, fb) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    setReviewFeedback(prev => ({ ...prev, [key]: fb }));
    fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/panel-review/feedback`), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_id: reviewerId, item_type: itemType, item_index: itemIndex, verdict: fb.verdict || '', comment: fb.comment || '' }),
    }).catch(console.error);
  };

  const submitAllFeedback = async () => {
    setFeedbackSubmitting(true);
    try {
      await Promise.all(Object.entries(reviewFeedback).map(([key, fb]) => {
        const [reviewerId, itemType, itemIndexStr] = key.split('::');
        return fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/panel-review/feedback`), {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewer_id: reviewerId, item_type: itemType, item_index: parseInt(itemIndexStr), verdict: fb.verdict || '', comment: fb.comment || '' }),
        });
      }));
      setFeedbackSubmitted(true);
      setTimeout(() => setFeedbackSubmitted(false), 2500);
    } catch (e) { alert(e.message); }
    finally { setFeedbackSubmitting(false); }
  };

  const runIteration = async () => {
    setIterating(true); setRevisions(null); setRevisionDrafts({}); setRevisionStatus({});
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/iterate`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Iteration failed');
      const data = await res.json();
      const revs = data.revisions || {};
      setRevisions(revs);
      const drafts = {};
      Object.entries(revs).forEach(([sec, v]) => { drafts[sec] = v.revised; });
      setRevisionDrafts(drafts);
      // Switch back to Draft tab so the user sees the diffs against the original.
      setTab('draft');
    } catch (err) { alert(err.message); }
    finally { setIterating(false); }
  };

  // Suggested-references accept / decline / undo. Persists status on
  // paper_sections._suggested_references; accept also adds the matched
  // OpenAlex work to the draft's collection.
  const [suggestedRefAction, setSuggestedRefAction] = useState({});  // {index: 'pending'}
  const actOnSuggestedRef = async (index, action) => {
    setSuggestedRefAction(prev => ({ ...prev, [index]: 'pending' }));
    try {
      const res = await fetch(
        getApiEndpoint(`/api/paper-drafts/${draft.id}/suggested-references/${index}/action`),
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
        },
      );
      if (!res.ok) throw new Error((await res.json()).detail || 'Action failed');
      if (onRefresh) onRefresh(draft.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setSuggestedRefAction(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  // ── Figure/table placeholder renderer ──────────────────────────────────────
  // Render citation keys with provenance tooltips
  const renderWithProvenance = (text) => {
    if (typeof text !== 'string' || !text.includes('[')) return text;
    const parts = text.split(/(\[[A-Z][a-z]+\d{4}[a-z]?\])/g);
    return parts.map((part, i) => {
      const match = part.match(/^\[([A-Z][a-z]+\d{4}[a-z]?)\]$/);
      if (match) {
        const key = match[1];
        const prov = citationProvenance[key];
        if (prov) {
          return (
            <span key={i} className="pa-citation" title={`${prov.title || key}\n\nWhy cited: ${prov.reason || 'N/A'}`}>
              [{key}]
            </span>
          );
        }
        return <span key={i} className="pa-citation">[{key}]</span>;
      }
      return part;
    });
  };

  const mdComponents = {
    p({ children }) {
      const text = Array.isArray(children)
        ? children.map(c => (typeof c === 'string' ? c : '')).join('')
        : typeof children === 'string' ? children : '';
      const figMatch = text.match(/^\[FIGURE:\s*(.+)\]$/i);
      const tableMatch = text.match(/^\[TABLE:\s*(.+)\]$/i);
      if (figMatch) return (
        <div className="pa-placeholder pa-placeholder--figure">
          <span className="pa-placeholder-icon">📊</span>
          <span><strong>Figure placeholder:</strong> {figMatch[1]}</span>
        </div>
      );
      if (tableMatch) return (
        <div className="pa-placeholder pa-placeholder--table">
          <span className="pa-placeholder-icon">📋</span>
          <span><strong>Table placeholder:</strong> {tableMatch[1]}</span>
        </div>
      );
      // Render citations with provenance tooltips
      const processed = Array.isArray(children)
        ? children.map(c => typeof c === 'string' ? renderWithProvenance(c) : c)
        : typeof children === 'string' ? renderWithProvenance(children) : children;
      return <p>{processed}</p>;
    },
  };

  // ── Draft tab ───────────────────────────────────────────────────────────────
  // Panel review object — referenced from both Draft and Peer Review tabs.
  const pr = draft.panel_review;

  const renderDraftTab = () => (
    <div className="ga-done-panel">
      <div className="pa-scaffold-notice">
        <strong>You are the author. This is your AI-assisted draft scaffold.</strong>
        <br />Every citation is grounded in your collection papers. Hover any <span className="pa-citation">[Citation]</span> to see why it was used. To complete:
        <ul>
          <li>Replace <code>[FIGURE: …]</code> and <code>[TABLE: …]</code> placeholders with real data</li>
          <li>Verify citations match your intended claims</li>
          <li>Refine each section — you decide what stays, what changes</li>
        </ul>
      </div>
      <div className="ga-done-panel-header">
        <div>
          <h3>{draft.title}</h3>
          <span className="ga-panel-tagline">{draft.paper_type} · {collectionName}{draft.target_venue ? ` · ${draft.target_venue}` : ''}</span>
          {canPickTeam && (
            <span style={{ position: 'relative', marginLeft: 8, display: 'inline-block' }}>
              {draft.team_id ? (
                <span className="ga-panel-tagline" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  🏷️ {currentTeamName() || 'Team'}
                  <button className="ga-btn ga-btn--sm ga-btn--ghost"
                    title="Detach team" onClick={() => attachTeam(null)}>
                    <FiX />
                  </button>
                </span>
              ) : (
                <>
                  <button className="ga-btn ga-btn--outline ga-btn--sm"
                    onClick={() => setShowTeamPicker(s => !s)}
                    title="Share this draft with a team">
                    <FiUsers /> Share with team
                  </button>
                  {showTeamPicker && (
                    <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 20,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                      padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 220 }}>
                      {userTeams.length === 0 && (
                        <p style={{ margin: 0, padding: 8, fontSize: 13, color: '#6b7280' }}>
                          You don't have any teams yet. Create one under Teams.
                        </p>
                      )}
                      {userTeams.map(t => (
                        <button key={t.id} className="ga-btn ga-btn--outline ga-btn--sm"
                          style={{ display: 'block', width: '100%', marginBottom: 4 }}
                          onClick={() => attachTeam(t.id)}>
                          <FiUsers /> {t.name}
                        </button>
                      ))}
                      <button className="ga-btn ga-btn--sm ga-btn--ghost"
                        style={{ display: 'block', width: '100%' }}
                        onClick={() => setShowTeamPicker(false)}>Cancel</button>
                    </div>
                  )}
                </>
              )}
            </span>
          )}
        </div>
        <div className="ga-done-actions">
          {teamMembers.length > 0 && (
            <>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => setShowRequestReview(true)}>
                <FiUsers /> Request Review
              </button>
              {showRequestReview && (
                <div className="ga-review-modal" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 10 }}>
                  <h4>Request review from:</h4>
                  {teamMembers.filter(m => m.user_id !== user?.id).map(m => (
                    <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => requestReview(m.user_id)}>
                      <FiUser /> {m.display_name}
                    </button>
                  ))}
                  <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setShowRequestReview(false)}>Cancel</button>
                </div>
              )}
            </>
          )}
          {hasUnsaved && (
            <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={saveSections} disabled={saving}>
              {saving ? <><FiLoader className="ga-spin" /> Saving…</> : <><FiCheck /> Save</>}
            </button>
          )}
          <button
            className="ga-btn ga-btn--primary ga-btn--sm"
            onClick={async () => {
              if (hasUnsaved || !pr) {
                setTab('review');
                await runPanelReview();
              } else {
                setTab('review');
              }
            }}
            disabled={runningPanel || saving}
            title={
              hasUnsaved
                ? 'Save accepted revisions and re-run the panel review on the new draft'
                : pr
                  ? 'Open the Panel Review step'
                  : 'Run a fresh panel review'
            }
          >
            {(runningPanel || saving)
              ? <><FiLoader className="ga-spin" /> {saving ? 'Saving…' : 'Reviewing…'}</>
              : hasUnsaved
                ? <><FiPlay /> Save & Re-review</>
                : pr
                  ? <><FiUsers /> Panel Review</>
                  : <><FiPlay /> Run Panel Review</>
            }
          </button>
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={rerunDraft} disabled={rerunning}>
            {rerunning ? <><FiLoader className="ga-spin" /> Regenerating…</> : <><FiRefreshCw /> Regenerate</>}
          </button>
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={reformatDraft} disabled={reformatting || !draft.paper_sections}>
            {reformatting ? <><FiLoader className="ga-spin" /> Reformatting…</> : <>Reformat</>}
          </button>
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
            const blob = new Blob([assembledDraft], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${draft.title.replace(/\s+/g, '_')}_draft.md`; a.click();
            URL.revokeObjectURL(url);
          }}>
            <FiDownload /> .md
          </button>
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() =>
            downloadAsDocx(assembledDraft, `${draft.title.replace(/\s+/g, '_')}_draft.docx`)
          }>
            <FiDownload /> .docx
          </button>
          {draft.draft_raw_text && (
            <button className="ga-btn ga-btn--sm" onClick={() => setShowRaw(s => !s)}>
              {showRaw ? 'Hide Raw' : 'View Raw'}
            </button>
          )}
        </div>
      </div>

      {(hasUnsaved || savedAt) && (
        <div className={`ga-save-hint${!hasUnsaved && savedAt ? ' ga-save-hint--saved' : ''}`}>
          {hasUnsaved
            ? '● Unsaved changes'
            : `✓ Saved at ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
        </div>
      )}

      {showRaw && draft.draft_raw_text && (
        <div className="ga-raw-view" style={{ marginBottom: 16 }}>
          <h4 className="ga-panel-section-title">Raw Generated Markdown</h4>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}>
            {draft.draft_raw_text}
          </pre>
        </div>
      )}

      {/* Iteration revisions from the latest panel review pass */}
      {renderRevisionsPanel()}

      {Object.entries(sections).map(([name, body]) => {
        const current = edits[name] !== undefined ? edits[name] : body;
        const isEditing = editingSection === name;
        const isExpanded = !!expandedSections[name];
        const toggle = () => setExpanded(s => ({ ...s, [name]: !s[name] }));
        const assigneeId = sectionAssignments[name];
        const assignable = teamMembers.length > 1;
        const assigneeOptions = teamMembers.filter(m => m.user_id !== user?.id);
        return (
          <div key={name} className={`ga-draft-section${isExpanded ? ' ga-draft-section--open' : ''}`}>
            <div className="ga-draft-section-header" onClick={toggle} role="button" tabIndex={0}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle()}>
              <span className="ga-draft-section-chevron">
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              </span>
              <h4>{name}{edits[name] !== undefined ? <span className="pa-edited-badge"> edited</span> : ''}</h4>
              {assignable && assigneeId && (
                <span style={{ position: 'relative', marginLeft: 'auto', marginRight: 8 }}
                  onClick={e => e.stopPropagation()}>
                  <button className="ga-btn ga-btn--sm ga-btn--outline"
                    title="Reassign or unassign this section"
                    onClick={() => setAssigningSection(assigningSection === name ? null : name)}>
                    <FiUser /> {teamMemberName(assigneeId)}
                  </button>
                  {assigningSection === name && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                      padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200 }}>
                      {assigneeOptions.map(m => (
                        <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm"
                          style={{ display: 'block', width: '100%', marginBottom: 4 }}
                          onClick={() => assignSection(name, m.user_id)}>
                          <FiUser /> {m.display_name || m.username}
                        </button>
                      ))}
                      <button className="ga-btn ga-btn--sm ga-btn--ghost"
                        style={{ display: 'block', width: '100%' }}
                        onClick={() => assignSection(name, null)}>
                        <FiX /> Unassign
                      </button>
                    </div>
                  )}
                </span>
              )}
              {assignable && !assigneeId && (
                <span style={{ position: 'relative', marginLeft: 'auto', marginRight: 8 }}
                  onClick={e => e.stopPropagation()}>
                  <button className="ga-btn ga-btn--sm ga-btn--ghost"
                    title="Assign this section to a teammate"
                    onClick={() => setAssigningSection(assigningSection === name ? null : name)}>
                    <FiUsers /> Assign
                  </button>
                  {assigningSection === name && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                      padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200 }}>
                      {assigneeOptions.map(m => (
                        <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm"
                          style={{ display: 'block', width: '100%', marginBottom: 4 }}
                          onClick={() => assignSection(name, m.user_id)}>
                          <FiUser /> {m.display_name || m.username}
                        </button>
                      ))}
                    </div>
                  )}
                </span>
              )}
              {isExpanded && !isEditing && (
                <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={e => { e.stopPropagation(); setEditing(name); }}>
                  <FiEdit2 /> Edit
                </button>
              )}
              {isExpanded && isEditing && (
                <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={e => { e.stopPropagation(); setEditing(null); }}>
                  <FiX /> Done
                </button>
              )}
            </div>
            {isExpanded && (
              isEditing ? (
                <textarea className="ga-section-editor" rows={12} value={current}
                  onChange={e => setEdits(d => ({ ...d, [name]: e.target.value }))} />
              ) : (
                <div className="ga-md-render">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{current}</ReactMarkdown>
                </div>
              )
            )}
          </div>
        );
      })}

      {/* Suggested references */}
      {suggestedRefs.length > 0 && (
        <div className="pa-suggested-refs">
          <h4>Suggested Additional References</h4>
          <p className="pa-suggested-refs-hint">
            These papers may strengthen your draft. <strong>Accept</strong> searches OpenAlex and adds the top match to your collection so it's available to cite next round.
          </p>
          {suggestedRefs.map((ref, i) => {
            const status = ref.status;
            const pending = suggestedRefAction[i] === 'pending';
            const cardCls =
              status === 'accepted' ? ' pa-suggested-ref-card--accepted'
              : status === 'declined' ? ' pa-suggested-ref-card--declined'
              : '';
            return (
              <div key={i} className={`pa-suggested-ref-card${cardCls}`}>
                <div className="pa-suggested-ref-title">
                  <strong>{ref.title}</strong>
                  {ref.authors && <span className="pa-suggested-ref-authors">{ref.authors}{ref.year ? `, ${ref.year}` : ''}</span>}
                </div>
                <p className="pa-suggested-ref-reason">{ref.reason}</p>
                <div className="pa-suggested-ref-actions">
                  {status === 'accepted' && (
                    <>
                      <span className="ga-revision-badge ga-revision-badge--accepted">
                        <FiCheck /> Added to collection{ref.matched_title && ref.matched_title !== ref.title ? ` (matched: ${ref.matched_title})` : ''}
                      </span>
                      <button
                        className="ga-btn ga-btn--sm ga-btn--ghost"
                        onClick={() => actOnSuggestedRef(i, 'undo')}
                        disabled={pending}
                      >Undo</button>
                    </>
                  )}
                  {status === 'declined' && (
                    <>
                      <span className="ga-revision-badge ga-revision-badge--skipped">Declined</span>
                      <button
                        className="ga-btn ga-btn--sm ga-btn--ghost"
                        onClick={() => actOnSuggestedRef(i, 'undo')}
                        disabled={pending}
                      >Undo</button>
                    </>
                  )}
                  {!status && (
                    <>
                      <button
                        className="ga-btn ga-btn--sm ga-btn--outline"
                        onClick={() => actOnSuggestedRef(i, 'decline')}
                        disabled={pending}
                      >Decline</button>
                      <button
                        className="ga-btn ga-btn--sm ga-btn--confirmed"
                        onClick={() => actOnSuggestedRef(i, 'accept')}
                        disabled={pending}
                        title="Search OpenAlex and add the top match to your collection"
                      >
                        {pending ? <><FiLoader className="ga-spin" /> Adding…</> : <><FiCheck /> Accept</>}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Peer Review tab ─────────────────────────────────────────────────────────
  const renderReviewTab = () => (
    <div className="ga-done-panel">
      <div className="ga-done-panel-header">
        <h3>Peer Review Panel</h3>
        <div className="ga-done-actions">
          {pr && (
            <>
              <button
                className={`ga-btn ga-btn--sm ${feedbackSubmitted ? 'ga-btn--success' : 'ga-btn--outline'}`}
                onClick={submitAllFeedback} disabled={feedbackSubmitting || runningPanel}
              >
                {feedbackSubmitting ? <><FiLoader className="ga-spin" /> Saving…</> : feedbackSubmitted ? <><FiCheck /> Saved</> : 'Submit Feedback'}
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
                const md = formatPanelReviewReport(pr, reviewFeedback, draft.title);
                const blob = new Blob([md], { type: 'text/markdown' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${(draft.title || 'review').replace(/\s+/g, '_')}_peer_review.md`; a.click();
                URL.revokeObjectURL(url);
              }}>
                <FiDownload /> .md
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
                const md = formatPanelReviewReport(pr, reviewFeedback, draft.title);
                downloadAsDocx(md, `${(draft.title || 'review').replace(/\s+/g, '_')}_peer_review.docx`);
              }}>
                <FiDownload /> .docx
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={runIteration}
                disabled={iterating || runningPanel}>
                {iterating ? <><FiLoader className="ga-spin" /> Iterating…</> : <><FiEdit2 /> Iterate Draft</>}
              </button>
            </>
          )}
          <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={runPanelReview} disabled={runningPanel || saving}>
            {(runningPanel || saving)
              ? <><FiLoader className="ga-spin" /> {saving ? 'Saving…' : 'Reviewing…'}</>
              : pr ? <><FiPlay /> Re-run Review</> : <><FiPlay /> Run Peer Review</>}
          </button>
          {/* Active iteration loop: advance to Done once the user is happy. */}
          {draft.current_stage === 'panel_review' && pr && onAdvance && (
            <button
              className="ga-btn ga-btn--confirmed ga-btn--sm"
              onClick={async () => {
                const ok = await saveSections();
                if (!ok) return;
                onAdvance({});
              }}
              disabled={runningPanel || iterating || saving}
              title="Lock in the current draft and finish the workflow"
            >
              Advance to Done <FiChevronRight />
            </button>
          )}
        </div>
      </div>

      {!pr && !runningPanel && (
        <div className="ga-panel-empty">
          <p className="ga-panel-empty-lead">
            Six specialist reviewers stress-test your paper — <strong>Methodology</strong>, <strong>Novelty</strong>, <strong>Related Work</strong>, <strong>Clarity</strong>, <strong>Reproducibility</strong>, and <strong>Impact</strong> — then an editor synthesises their findings.
          </p>
        </div>
      )}

      {runningPanel && (
        <div className="ga-panel-running">
          <FiLoader className="ga-spin ga-panel-running-icon" />
          <p>Running 6 specialist reviewers in parallel…</p>
          <p className="ga-hint">Each reviewer has read your full draft. This takes 30–60 seconds.</p>
        </div>
      )}

      {pr && !runningPanel && (() => {
        const syn = pr.synthesis || {};
        const decisionClass = {
          accept: 'pa-decision--accept',
          minor_revision: 'pa-decision--minor',
          major_revision: 'pa-decision--major',
          reject: 'pa-decision--reject',
        }[syn.decision] || '';

        return (
          <>
            {/* Editorial summary */}
            <div className="pa-editorial-summary">
              <div className="pa-editorial-header">
                <span className={`pa-decision-badge ${decisionClass}`}>
                  {(syn.decision || '—').replace(/_/g, ' ').toUpperCase()}
                </span>
                <span className="pa-overall-score">Score: {syn.overall_score ?? '—'}/10</span>
              </div>
              <p>{syn.editorial_summary}</p>
            </div>

            {/* Panel Strengths */}
            {(syn.top_strengths || []).length > 0 && (
              <div className="ga-panel-section">
                <h4 className="ga-panel-section-title ga-panel-section-title--strengths">✓ Panel Strengths</h4>
                <ul className="ga-panel-strengths-list">
                  {syn.top_strengths.map((s, i) => {
                    const fb = getReviewFeedback('__synthesis__', 'top_strengths', i);
                    return (
                      <li key={i} className={fb?.verdict === 'agree' ? 'ga-issue--agreed' : fb?.verdict === 'disagree' ? 'ga-issue--disagreed' : ''}>
                        <span>{s}</span>
                        <FeedbackButtons feedback={fb} onFeedback={f => saveReviewFeedback('__synthesis__', 'top_strengths', i, f)} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Priority Issues */}
            {(syn.ranked_issues || []).length > 0 && (
              <div className="ga-panel-section">
                <h4 className="ga-panel-section-title ga-panel-section-title--issues">
                  Priority Issues ({syn.ranked_issues.length})
                </h4>
                {syn.ranked_issues.map((issue, i) => {
                  const fb = getReviewFeedback('__synthesis__', 'ranked_issues', i);
                  return (
                    <div key={i} className={`ga-panel-issue ga-panel-issue--${issue.severity}${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                      <div className="ga-panel-issue-header">
                        <span className={`ga-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                          {SEVERITY_META[issue.severity]?.label || issue.severity}
                        </span>
                        <span className="ga-panel-issue-section">{issue.section}</span>
                      </div>
                      <p className="ga-panel-issue-text">{issue.issue}</p>
                      {issue.fix && <p className="ga-panel-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                      <FeedbackButtons feedback={fb} onFeedback={f => saveReviewFeedback('__synthesis__', 'ranked_issues', i, f)} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Individual reviewer cards */}
            <div className="ga-panel-section">
              <h4 className="ga-panel-section-title">Individual Reviewer Feedback</h4>
              {(pr.reviewers || []).map(rv => {
                const isOpen = !!expandedReviewers[rv.id];
                const highCount = (rv.issues || []).filter(i => i.severity === 'high').length;
                return (
                  <div key={rv.id} className="ga-reviewer-card">
                    <div className="ga-reviewer-card-header"
                      onClick={() => setExpandedReviewers(s => ({ ...s, [rv.id]: !s[rv.id] }))}
                      role="button" tabIndex={0}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setExpandedReviewers(s => ({ ...s, [rv.id]: !s[rv.id] }))}>
                      <span className="ga-reviewer-label">{rv.name}</span>
                      {rv.score != null && <span className="pa-reviewer-score">{rv.score}/10</span>}
                      {highCount > 0 && <span className="ga-reviewer-high-count">{highCount} high</span>}
                      <span className="ga-reviewer-issue-count">{(rv.issues || []).length} issue{(rv.issues || []).length !== 1 ? 's' : ''}</span>
                      <span className="ga-reviewer-chevron">{isOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
                    </div>
                    {isOpen && (
                      <div className="ga-reviewer-card-body">
                        {rv.summary && <p className="ga-reviewer-comment">{rv.summary}</p>}
                        {(rv.strengths || []).length > 0 && (
                          <div className="ga-reviewer-strengths">
                            <strong>Strengths:</strong>
                            <ul>{rv.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                          </div>
                        )}
                        {(rv.issues || []).map((issue, i) => {
                          const fb = getReviewFeedback(rv.id, 'issues', i);
                          return (
                            <div key={i} className={`ga-panel-issue ga-panel-issue--${issue.severity}${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                              <div className="ga-panel-issue-header">
                                <span className={`ga-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                                  {SEVERITY_META[issue.severity]?.label || issue.severity}
                                </span>
                                <span className="ga-panel-issue-section">{issue.section}</span>
                              </div>
                              <p className="ga-panel-issue-text">{issue.issue}</p>
                              {issue.fix && <p className="ga-panel-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                              <FeedbackButtons feedback={fb} onFeedback={f => saveReviewFeedback(rv.id, 'issues', i, f)} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Revisions live on the Draft tab. Surface a hint here when iterating. */}
            {iterating && <div className="ga-revision-loading"><FiLoader className="ga-spin" /> Rewriting sections — switching to the Draft step…</div>}
            {revisions && Object.keys(revisions).length > 0 && !iterating && (
              <div className="ga-revision-empty">
                Revisions ready in the <button className="ga-btn-link" onClick={() => setTab('draft')}>Draft step</button>.
              </div>
            )}
          </>
        );
      })()}
    </div>
  );

  // Reusable revisions panel — rendered inside the Draft tab so accept/decline
  // happens in the same view as the running draft text.
  const renderRevisionsPanel = () => {
    if (iterating) {
      return <div className="ga-revision-loading"><FiLoader className="ga-spin" /> Rewriting sections to address reviewer issues…</div>;
    }
    if (!revisions || Object.keys(revisions).length === 0) return null;

    const allVerdicts = Object.values(revisions).flatMap(rev =>
      (rev.issues_addressed || []).map(ia => ia.verdict || 'unknown')
    );
    const counts = allVerdicts.reduce((acc, v) => { acc[v] = (acc[v] || 0) + 1; return acc; }, {});
    const totalChecked = allVerdicts.filter(v => v && v !== 'unknown').length;

    return (
      <div className="ga-revision-panel">
        <h4 className="ga-panel-section-title ga-panel-section-title--revisions">
          Proposed Revisions ({Object.keys(revisions).length} section{Object.keys(revisions).length !== 1 ? 's' : ''})
        </h4>
        {totalChecked > 0 && (
          <div className="ga-verify-summary">
            <span className="ga-verify-summary-label">Verification:</span>
            {counts.resolved > 0 && (<span className="ga-verify-pill ga-verify-pill--resolved"><FiCheck /> {counts.resolved} resolved</span>)}
            {counts.partial > 0 && (<span className="ga-verify-pill ga-verify-pill--partial">{counts.partial} partial</span>)}
            {counts.unresolved > 0 && (<span className="ga-verify-pill ga-verify-pill--unresolved">{counts.unresolved} unresolved</span>)}
            {counts.regressed > 0 && (<span className="ga-verify-pill ga-verify-pill--regressed">⚠ {counts.regressed} regressed</span>)}
            <span className="ga-verify-summary-hint">— each revision was checked against the issue it claims to fix.</span>
          </div>
        )}
        {!isGuideSeen(user?.id, 'iteration_guide') && (
          <div className="pa-iteration-guide">
            <strong>How to iterate your draft:</strong>
            <div className="pa-iteration-steps">
              <span>&#9312; Review each issue — Accept or Decline the fix</span>
              <span>&#9313; Accepted fixes appear as unsaved edits in the Draft tab</span>
              <span>&#9314; Click "Save & Re-review" to commit changes and re-run the panel</span>
              <span>&#9315; Repeat until no high-severity issues remain</span>
            </div>
            <button onClick={(e) => { e.target.closest('.pa-iteration-guide').remove(); markGuideSeen(user?.id, 'iteration_guide'); }}>Got it</button>
          </div>
        )}
        <p className="ga-revision-instructions">
          Edit each revision as needed, then Accept to stage it for saving — or Decline to leave the section unchanged. Click <strong>Save & Re-review</strong> at the top to commit and re-run the panel on the new draft.
        </p>
        {Object.entries(revisions).map(([sectionName, rev]) => {
          const status = revisionStatus[sectionName];
          const isCollapsed = collapsedRevisions[sectionName];
          const original = sections[sectionName] || '';
          const draftText = revisionDrafts[sectionName] ?? rev.revised;
          return (
            <div key={sectionName} className={`ga-revision-card${status === 'kept-revised' ? ' ga-revision-card--accepted' : status === 'kept-original' ? ' ga-revision-card--skipped' : status === 'saved-edit' ? ' ga-revision-card--accepted' : ''}`}>
              <div className="ga-revision-card-header" onClick={() => setCollapsedRevisions(p => ({ ...p, [sectionName]: !p[sectionName] }))} style={{ cursor: 'pointer' }}>
                <span className="ga-revision-section-name">
                  {isCollapsed ? <FiChevronRight /> : <FiChevronDown />} {sectionName}
                </span>
                <div className="ga-revision-actions" onClick={e => e.stopPropagation()}>
                  {status === 'kept-original' && <span className="ga-revision-badge ga-revision-badge--skipped">Original kept</span>}
                  {status === 'kept-revised' && <span className="ga-revision-badge ga-revision-badge--accepted"><FiCheck /> Revised accepted</span>}
                  {status === 'saved-edit' && <span className="ga-revision-badge ga-revision-badge--accepted"><FiCheck /> Custom edit saved</span>}
                  {status && (
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => {
                      setRevisionStatus(p => ({ ...p, [sectionName]: undefined }));
                      setEdits(p => { const n = {...p}; delete n[sectionName]; return n; });
                    }}>Undo</button>
                  )}
                </div>
              </div>
              {!isCollapsed && (
                <>
                  <div className="ga-revision-issues">
                    {(rev.issues_addressed || []).map((iss, i) => (
                      <span key={i} className="ga-issue-tag-group">
                        <span className={`ga-severity-badge ${SEVERITY_META[iss.severity]?.cls || ''}`}>
                          {SEVERITY_META[iss.severity]?.label || iss.severity} · {iss.issue?.slice(0, 60)}
                        </span>
                        {iss.verdict && iss.verdict !== 'unknown' && (
                          <span className={`ga-verify-badge ga-verify-badge--${iss.verdict}`} title={iss.rationale || ''}>
                            {iss.verdict === 'resolved' && <><FiCheck /> resolved</>}
                            {iss.verdict === 'partial' && 'partial'}
                            {iss.verdict === 'unresolved' && 'unresolved'}
                            {iss.verdict === 'regressed' && '⚠ regressed'}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  {!status && (
                    <div className="ga-revision-choices">
                      <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() =>
                        setRevisionStatus(p => ({ ...p, [sectionName]: 'kept-original' }))
                      }>Decline</button>
                      <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() =>
                        setRevisionStatus(p => ({ ...p, [sectionName]: 'editing' }))
                      }><FiEdit2 /> Custom edit</button>
                      <button className="ga-btn ga-btn--sm ga-btn--confirmed" onClick={() => {
                        setEdits(p => ({ ...p, [sectionName]: rev.revised }));
                        setRevisionStatus(p => ({ ...p, [sectionName]: 'kept-revised' }));
                      }}><FiCheck /> Accept</button>
                    </div>
                  )}
                  {status === 'editing' && (
                    <div className="ga-revision-edit-area">
                      <div className="ga-revision-edit-start">
                        Start from:
                        <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setRevisionDrafts(p => ({ ...p, [sectionName]: original }))}>original</button>
                        <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setRevisionDrafts(p => ({ ...p, [sectionName]: rev.revised }))}>revised</button>
                      </div>
                      <textarea className="ga-revision-body" value={draftText}
                        onChange={e => setRevisionDrafts(p => ({ ...p, [sectionName]: e.target.value }))}
                        rows={Math.max(6, draftText.split('\n').length + 2)} />
                      <div className="ga-revision-edit-actions">
                        <button className="ga-btn ga-btn--sm ga-btn--confirmed" onClick={() => {
                          setEdits(p => ({ ...p, [sectionName]: draftText }));
                          setRevisionStatus(p => ({ ...p, [sectionName]: 'saved-edit' }));
                        }}><FiCheck /> Save edit</button>
                        <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() =>
                          setRevisionStatus(p => ({ ...p, [sectionName]: undefined }))
                        }>Cancel</button>
                      </div>
                    </div>
                  )}
                  {!status && original && rev.revised && (
                    <UnifiedDiff original={original} revised={rev.revised} />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Results tab — simplified outputs after the user has accepted the draft.
  const renderResultsTab = () => (
    <div className="ga-done-panel ga-results-panel">
      <div className="ga-done-panel-header">
        <div>
          <h3>Results</h3>
          <span className="ga-panel-tagline">Submit your paper and track the outcome</span>
        </div>
        <div className="ga-done-actions">
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
            const blob = new Blob([assembledDraft], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `${draft.title.replace(/\s+/g, '_')}_draft.md`; a.click();
            URL.revokeObjectURL(url);
          }}><FiDownload /> Draft .md</button>
          <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() =>
            downloadAsDocx(assembledDraft, `${draft.title.replace(/\s+/g, '_')}_draft.docx`)
          }><FiDownload /> Draft .docx</button>
        </div>
      </div>
      <div className="ga-results-section">
        <h4 className="ga-results-section-title">Submission status</h4>
        <p className="ga-hint">
          Status: <strong>{draft.submission_status || 'draft'}</strong>
          {draft.submission_date ? ` · submitted ${new Date(draft.submission_date).toLocaleDateString()}` : ''}
        </p>
      </div>
    </div>
  );

  return (
    <div className="ga-done-stage">
      {/* The stepper drives navigation when the parent passes a tab prop, so
          hide the in-stage tab bar in that case. */}
      {tabProp == null && (
        <div className="ga-done-tabs">
          <button className={`ga-tab${tab === 'draft' ? ' ga-tab--active' : ''}`} onClick={() => setTab('draft')}>
            <FiClipboard /> Draft
          </button>
          <button className={`ga-tab${tab === 'review' ? ' ga-tab--active' : ''}`} onClick={() => setTab('review')}>
            <FiUsers /> Panel Review
          </button>
          {draft.current_stage === 'done' && (
            <button className={`ga-tab${tab === 'results' ? ' ga-tab--active' : ''}`} onClick={() => setTab('results')}>
              <FiFileText /> Results
            </button>
          )}
        </div>
      )}
      {tab === 'draft'   && renderDraftTab()}
      {tab === 'review'  && renderReviewTab()}
      {tab === 'results' && renderResultsTab()}
    </div>
  );
}

// ── Stage panels ──────────────────────────────────────────────────────────────

function CollectionInputStage({ draft, collections, onAdvance, readOnly }) {
  const [form, setForm] = useState({
    title:             draft.title || '',
    paper_type:        draft.paper_type || 'journal',
    target_venue:      draft.target_venue || '',
    research_question: draft.research_question || '',
  });
  const [loading, setLoading] = useState(false);
  const [futureDirections, setFutureDirections] = useState(null);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setLoading(true);
    try {
      await onAdvance(form);
    } catch (e) {
      alert(e.message || 'Failed to generate outline');
    } finally { setLoading(false); }
  };

  const loadIdeas = async () => {
    setLoadingIdeas(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/paper-drafts/collection-ideas/${draft.collection_id}`),
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to generate ideas');
      const data = await res.json();
      setFutureDirections(data.futureDirections || '');
      setIdeasOpen(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoadingIdeas(false);
    }
  };

  const collectionName = (collections || []).find(c => c.id === draft.collection_id)?.name || `Collection #${draft.collection_id}`;

  return (
    <div className="ga-stage-content">
      <p className="ga-stage-desc">
        Describe your paper. The system will analyse your collection and generate a tailored outline.
      </p>
      {draft.collection_id && (
        <div className="pa-collection-link">
          <span>Writing from: <strong>{collectionName || `Collection #${draft.collection_id}`}</strong></span>
          <a
            href={`#collection-${draft.collection_id}`}
            onClick={(e) => {
              e.preventDefault();
              window.open(`${window.location.origin}?view=collections&id=${draft.collection_id}`, '_blank');
            }}
            className="pa-edit-collection-btn"
          >
            Add more papers to this collection
          </a>
        </div>
      )}
      <div className="ga-form-group">
        <label>Paper Title</label>
        <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          disabled={!!readOnly}
          placeholder="e.g. A Survey of Transformer Architectures for NLP" />
      </div>
      <div className="ga-form-row">
        <div className="ga-form-group">
          <label>Paper Type</label>
          <select value={form.paper_type} onChange={e => setForm(p => ({ ...p, paper_type: e.target.value }))} disabled={!!readOnly}>
            {PAPER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="ga-form-group">
          <label>Target Venue <span className="ga-label-hint">(optional)</span></label>
          <input value={form.target_venue} onChange={e => setForm(p => ({ ...p, target_venue: e.target.value }))}
            disabled={!!readOnly}
            placeholder="e.g. Nature, NeurIPS, PLOS ONE" />
        </div>
      </div>

      {/* Future directions panel */}
      <div className="pa-ideas-panel">
        {!readOnly && (
        <button
          className="pa-ideas-toggle"
          onClick={futureDirections ? () => setIdeasOpen(o => !o) : loadIdeas}
          disabled={loadingIdeas}
          type="button"
        >
          {loadingIdeas
            ? <><FiLoader className="ga-spin" size={13} /> Analysing collection for research gaps…</>
            : futureDirections
              ? <><span className="pa-ideas-toggle-arrow">{ideasOpen ? '▾' : '▸'}</span> Research ideas from "{collectionName}"</>
              : <><FiRefreshCw size={13} /> Generate research ideas from "{collectionName}"</>}
        </button>
        )}
        {ideasOpen && futureDirections && (
          <div className="pa-ideas-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{futureDirections}</ReactMarkdown>
          </div>
        )}
      </div>

      <div className="ga-form-group">
        <label>Research Question / Gap <span className="ga-label-hint">(optional but recommended)</span></label>
        <textarea rows={3} value={form.research_question}
          onChange={e => setForm(p => ({ ...p, research_question: e.target.value }))}
          disabled={!!readOnly}
          placeholder="What gap does this paper fill? Use the ideas above for inspiration." />
      </div>
      {!readOnly && (
        <div className="ga-stage-actions">
          <button className="ga-btn ga-btn--primary" onClick={handleSubmit} disabled={loading || !form.title.trim()}>
            {loading ? <><FiLoader className="ga-spin" /> Generating Outline…</> : <>Generate Outline <FiChevronRight /></>}
          </button>
        </div>
      )}
    </div>
  );
}

// A key_point may be a plain string or a structured object the model sometimes
// returns ({point, detail, sub_bullets}). Normalize to display text so it never
// gets rendered as a raw object (React error #31).
const kpText = (pt) => (typeof pt === 'string' || typeof pt === 'number')
  ? String(pt)
  : (pt?.point ?? pt?.detail ?? '');

function OutlineStage({ draft, onAdvance, onReset, readOnly }) {
  const outlineKey = `pa_outline_${draft.id}`;
  const [advancing, setAdvancing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newTheme, setNewTheme] = useState('');

  // Local edits — initialised from localStorage or null (meaning "use draft.outline as-is")
  const [edits, setEdits] = useState(() => {
    try { const s = localStorage.getItem(outlineKey); if (s) return JSON.parse(s); } catch {}
    return null;
  });

  const outline = edits || draft.outline;
  const isModified = edits !== null;

  useEffect(() => {
    if (edits) { try { localStorage.setItem(outlineKey, JSON.stringify(edits)); } catch {} }
  }, [edits, outlineKey]);

  // Helpers to update edits
  const patch = (updater) =>
    setEdits(prev => updater({ ...(prev || draft.outline) }));

  const patchSection = (i, field, value) =>
    patch(o => ({ ...o, sections: o.sections.map((s, idx) => idx === i ? { ...s, [field]: value } : s) }));

  const patchPoint = (si, pi, value) =>
    patchSection(si, 'key_points', outline.sections[si].key_points.map((p, j) =>
      j === pi ? (p && typeof p === 'object' ? { ...p, point: value } : value) : p));

  const removePoint = (si, pi) =>
    patchSection(si, 'key_points', outline.sections[si].key_points.filter((_, j) => j !== pi));

  const addPoint = (si) =>
    patchSection(si, 'key_points', [...(outline.sections[si].key_points || []), '']);

  const removeTheme = (i) =>
    patch(o => ({ ...o, key_themes: o.key_themes.filter((_, idx) => idx !== i) }));

  const addTheme = () => {
    const t = newTheme.trim();
    if (!t) return;
    patch(o => ({ ...o, key_themes: [...(o.key_themes || []), t] }));
    setNewTheme('');
  };

  const resetEdits = () => {
    setEdits(null);
    try { localStorage.removeItem(outlineKey); } catch {}
  };

  const handleApprove = async () => {
    setAdvancing(true);
    try {
      await onAdvance({ outline });
      try { localStorage.removeItem(outlineKey); } catch {}
    } catch (e) {
      alert(e.message || 'Advance failed');
    } finally { setAdvancing(false); }
  };

  const handleRegenerate = async () => {
    setResetting(true);
    try {
      resetEdits();
      await onReset();
    } catch (e) {
      alert(e.message || 'Regeneration failed');
    } finally { setResetting(false); }
  };

  if (resetting || draft.stage_status === 'running' || draft.stage_status === 'pending') {
    return (
      <div className="ga-stage-content ga-stage-loading">
        <FiLoader className="ga-spin" size={24} />
        <p>Analysing your collection and generating a tailored outline…</p>
      </div>
    );
  }

  if (!outline) {
    return (
      <div className="ga-stage-content">
        <p className="ga-stage-desc">Outline generation failed or is not yet available.</p>
        <div className="ga-stage-actions">
          <button className="ga-btn ga-btn--outline" onClick={handleRegenerate} disabled={resetting}>
            <FiRefreshCw /> Regenerate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-stage-content">
      <div className="pa-outline-header">
        <p className="ga-stage-desc" style={{ margin: 0 }}>
          Review and optionally edit the outline before proceeding.
        </p>
        <div className="pa-outline-header-actions">
          {isModified && (
            <button className="pa-outline-reset-link" onClick={resetEdits} type="button">
              Reset to AI version
            </button>
          )}
          <button
            className={`ga-btn ga-btn--sm ${editing ? 'ga-btn--primary' : 'ga-btn--outline'}`}
            onClick={() => setEditing(e => !e)}
            type="button"
          >
            {editing ? <><FiCheck size={13} /> Done editing</> : <><FiEdit2 size={13} /> Edit outline</>}
          </button>
        </div>
      </div>

      {/* Central argument */}
      {editing ? (
        <div className="ga-form-group" style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Central argument</label>
          <textarea
            rows={2}
            className="pa-outline-edit-input"
            value={outline.central_argument || ''}
            onChange={e => patch(o => ({ ...o, central_argument: e.target.value }))}
          />
        </div>
      ) : outline.central_argument ? (
        <div className="pa-central-argument">
          <strong>Central argument:</strong> {outline.central_argument}
        </div>
      ) : null}

      {/* Key themes */}
      {((outline.key_themes || []).length > 0 || editing) && (
        <div className="pa-themes">
          <strong>Key themes: </strong>
          {(outline.key_themes || []).map((t, i) => (
            <span key={i} className="pa-theme-chip">
              {kpText(t)}
              {editing && (
                <button className="pa-theme-remove" onClick={() => removeTheme(i)} type="button">×</button>
              )}
            </span>
          ))}
          {editing && (
            <span className="pa-theme-add">
              <input
                className="pa-theme-input"
                value={newTheme}
                onChange={e => setNewTheme(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTheme()}
                placeholder="Add theme…"
              />
              <button className="pa-outline-add-btn" onClick={addTheme} type="button">+</button>
            </span>
          )}
        </div>
      )}

      {/* Sections */}
      <div className="pa-outline-sections">
        {(outline.sections || []).map((sec, i) => (
          <div key={i} className="pa-outline-section">
            <div className="pa-outline-section-name">
              <span className="pa-outline-num">{i + 1}.</span>
              {editing ? (
                <input
                  className="pa-outline-edit-input pa-outline-name-input"
                  value={sec.name}
                  onChange={e => patchSection(i, 'name', e.target.value)}
                />
              ) : (
                <strong>{sec.name}</strong>
              )}
            </div>
            {editing ? (
              <input
                className="pa-outline-edit-input"
                value={sec.purpose || ''}
                onChange={e => patchSection(i, 'purpose', e.target.value)}
                placeholder="Section purpose…"
                style={{ marginBottom: 6 }}
              />
            ) : (
              <p className="pa-outline-purpose">{sec.purpose}</p>
            )}
            {(editing || (sec.key_points || []).length > 0) && (
              <ul className="pa-outline-points">
                {(sec.key_points || []).map((pt, j) => (
                  <li key={j}>
                    {editing ? (
                      <span className="pa-kp-row">
                        <input
                          className="pa-outline-edit-input"
                          value={kpText(pt)}
                          onChange={e => patchPoint(i, j, e.target.value)}
                        />
                        <button className="pa-kp-remove" onClick={() => removePoint(i, j)} type="button">×</button>
                      </span>
                    ) : (
                      <>
                        {kpText(pt)}
                        {pt && typeof pt === 'object' && pt.detail && pt.point && (
                          <span className="pa-kp-detail"> — {pt.detail}</span>
                        )}
                        {pt && typeof pt === 'object' && Array.isArray(pt.sub_bullets) && pt.sub_bullets.length > 0 && (
                          <ul className="pa-outline-points">
                            {pt.sub_bullets.map((sb, k) => <li key={k}>{kpText(sb)}</li>)}
                          </ul>
                        )}
                      </>
                    )}
                  </li>
                ))}
                {editing && (
                  <li>
                    <button className="pa-outline-add-btn" onClick={() => addPoint(i)} type="button">
                      + Add point
                    </button>
                  </li>
                )}
              </ul>
            )}
            {(sec.suggested_citations || []).length > 0 && (
              <p className="pa-outline-cites">
                Cite: {sec.suggested_citations.map((c, j) => <code key={j}>[{c}]</code>)
                  .reduce((acc, el) => acc.length ? [...acc, ' ', el] : [el], [])}
              </p>
            )}
          </div>
        ))}
      </div>

      {isModified && !editing && (
        <p className="pa-modified-note">Outline has been edited — your changes will be used for drafting.</p>
      )}

      {!readOnly && (
        <div className="ga-stage-actions">
          <button className="ga-btn ga-btn--outline" onClick={handleRegenerate} disabled={resetting || advancing}>
            {resetting ? <><FiLoader className="ga-spin" /> Regenerating…</> : <><FiRefreshCw /> Regenerate</>}
          </button>
          <button className="ga-btn ga-btn--primary" onClick={handleApprove} disabled={advancing || resetting}>
            {advancing ? <><FiLoader className="ga-spin" /> Saving…</> : <>Use this outline — Add Author Info <FiChevronRight /></>}
          </button>
        </div>
      )}
    </div>
  );
}

function AuthorProfileStage({ draft, onAdvance, readOnly }) {
  const draftKey = `pa_author_${draft.id}`;
  const [form, setForm] = useState(() => {
    try { const s = localStorage.getItem(draftKey); if (s) return JSON.parse(s); } catch {}
    return draft.author_profile || { author_name: '', institution: '', key_claims: '', novelty: '', acknowledgments: '' };
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch {}
  }, [form, draftKey]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // If viewing this form from a later stage (e.g., drafting error), move back first
      if (draft.current_stage !== 'author_profile') {
        await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/goto`), {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_stage: 'author_profile' }),
        });
      }
      await onAdvance(form);
      try { localStorage.removeItem(draftKey); } catch {}
    } catch (e) {
      alert(e.message || 'Draft generation failed');
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="ga-stage-content ga-stage-loading">
        <FiLoader className="ga-spin" size={24} />
        <p>Writing your paper draft with citations from the collection…</p>
        <p className="ga-hint">This may take a minute.</p>
      </div>
    );
  }

  return (
    <div className="ga-stage-content">
      <p className="ga-stage-desc">
        Provide author context and state the paper's key contributions. This guides the draft's framing and voice.
      </p>
      <div className="ga-form-row">
        <div className="ga-form-group">
          <label>Author Name(s)</label>
          <input value={form.author_name || ''} onChange={e => setForm(p => ({ ...p, author_name: e.target.value }))}
            disabled={!!readOnly}
            placeholder="e.g. Jane Smith, John Doe" />
        </div>
        <div className="ga-form-group">
          <label>Institution(s)</label>
          <input value={form.institution || ''} onChange={e => setForm(p => ({ ...p, institution: e.target.value }))}
            disabled={!!readOnly}
            placeholder="e.g. MIT, Stanford University" />
        </div>
      </div>
      <div className="ga-form-group">
        <label>Key Claims / Contributions</label>
        <textarea rows={4} value={form.key_claims || ''}
          onChange={e => setForm(p => ({ ...p, key_claims: e.target.value }))}
          disabled={!!readOnly}
          placeholder="What are the 2-3 main contributions of this paper?" />
      </div>
      <div className="ga-form-group">
        <label>Novelty Statement</label>
        <textarea rows={3} value={form.novelty || ''}
          onChange={e => setForm(p => ({ ...p, novelty: e.target.value }))}
          disabled={!!readOnly}
          placeholder="How does this work differ from or improve upon existing literature?" />
      </div>
      <div className="ga-form-group">
        <label>Acknowledgments <span className="ga-label-hint">(optional)</span></label>
        <input value={form.acknowledgments || ''}
          onChange={e => setForm(p => ({ ...p, acknowledgments: e.target.value }))}
          disabled={!!readOnly}
          placeholder="Funding sources, collaborators, etc." />
      </div>
      <div className="ga-stage-actions">
        <button className="ga-btn ga-btn--primary" onClick={handleSubmit} disabled={loading || !!readOnly}>
          {loading ? <><FiLoader className="ga-spin" /> Generating Draft…</> : <>Generate Draft <FiChevronRight /></>}
        </button>
      </div>
    </div>
  );
}

function DraftingStage({ draft, onRefresh }) {
  useEffect(() => {
    if (draft.stage_status !== 'done' && draft.stage_status !== 'error') {
      const id = setInterval(() => onRefresh(draft.id), 5000);
      return () => clearInterval(id);
    }
  }, [draft.id, draft.stage_status, onRefresh]);

  // If an existing draft is available, show it (read-only preview)
  if (draft.paper_sections && draft.stage_status !== 'error') {
    const sections = draft.paper_sections || {};
    const assembledDraft = Object.entries(sections)
      .map(([name, body]) => `## ${name}\n\n${body}`)
      .join('\n\n');

    const downloadMd = () => {
      const blob = new Blob([assembledDraft], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${(draft.title || 'paper').replace(/\s+/g, '_')}_draft.md`; a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="ga-stage-content">
        <div className="ga-done-panel-header" style={{ marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>{draft.title}</h3>
            <span className="ga-panel-tagline">Existing draft preview</span>
          </div>
          <div className="ga-done-actions">
            <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={downloadMd}>
              <FiDownload /> .md
            </button>
            <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() =>
              downloadAsDocx(assembledDraft, `${(draft.title || 'paper').replace(/\s+/g, '_')}_draft.docx`)
            }>
              <FiDownload /> .docx
            </button>
          </div>
        </div>
        <div className="ga-draft-results">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{assembledDraft}</ReactMarkdown>
        </div>
      </div>
    );
  }

  if (draft.stage_status === 'error') {
    const regenerate = async () => {
      try {
        const res = await fetch(getApiEndpoint(`/api/paper-drafts/${draft.id}/reset`), {
          method: 'POST', headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Regenerate failed');
        if (onRefresh) onRefresh(draft.id);
      } catch (e) { alert(e.message); }
    };
    return (
      <div className="ga-stage-content">
        <p style={{ color: '#b91c1c', marginBottom: 12 }}>
          Draft generation failed. Click <strong>Regenerate</strong> to try again — you don't need to go back to the Author Profile step unless the author data needs changes.
        </p>
        {draft.error_detail && (
          <div className="ga-error" style={{ whiteSpace: 'pre-wrap', marginTop: 8, marginBottom: 12 }}>
            {String(draft.error_detail)}
          </div>
        )}
        <button className="ga-btn ga-btn--primary" onClick={regenerate}>
          <FiRefreshCw /> Regenerate
        </button>
      </div>
    );
  }

  return (
    <div className="ga-stage-content ga-stage-loading">
      <FiLoader className="ga-spin" size={24} />
      <p>Generating draft scaffold section by section…</p>
      <p className="ga-hint">Each section is written individually. This takes 1–3 minutes. The page updates automatically.</p>
      <p className="ga-hint">The result will be a structural draft — you will need to add real figures, experimental data, and verify citations.</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PaperAgent({ initialCollectionId = null, initialIdea = null, initialDraftId = null, user = null, onMounted } = {}) {
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState(initialDraftId);
  const [viewStage, setViewStage] = useState(null);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('pa_sidebar_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('pa_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);
  const [showStageHints, setShowStageHints] = useState(() => !isGuideSeen(user?.id, 'stage_hints'));
  const [newForm, setNewForm] = useState({
    title: initialIdea?.title || '',
    collection_id: initialCollectionId ? String(initialCollectionId) : '',
    paper_type: 'journal',
    target_venue: '',
    research_question: initialIdea
      ? `${initialIdea.gap}\n\n${initialIdea.description}`
      : '',
  });

  const loadDrafts = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/paper-drafts'), { headers: getAuthHeaders() });
      if (res.ok) setDrafts(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  const loadCollections = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/collections'), { headers: getAuthHeaders() });
      if (res.ok) setCollections(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    loadDrafts();
    loadCollections();
    if (initialCollectionId) setShowCreate(true);
    if (onMounted) onMounted();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDraft = useCallback(async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/paper-drafts/${id}`), { headers: getAuthHeaders() });
      if (res.ok) {
        const updated = await res.json();
        setDrafts(prev => prev.map(d => d.id === id ? updated : d));
      }
    } catch {}
  }, []);

  const createDraft = async () => {
    if (!newForm.title.trim() || !newForm.collection_id) return;
    setCreating(true);
    try {
      const res = await fetch(getApiEndpoint('/api/paper-drafts'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newForm, collection_id: parseInt(newForm.collection_id) }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Create failed');
      const created = await res.json();
      setDrafts(prev => [created, ...prev]);
      setSelectedId(created.id);
      setShowCreate(false);
      setNewForm({ title: '', collection_id: '', paper_type: 'journal', target_venue: '', research_question: '' });
    } catch (e) { alert(e.message); }
    finally { setCreating(false); }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this paper draft? This cannot be undone.')) return;
    try {
      await fetch(getApiEndpoint(`/api/paper-drafts/${id}`), { method: 'DELETE', headers: getAuthHeaders() });
      setDrafts(prev => prev.filter(d => d.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch {}
  };

  const advanceStage = useCallback(async (data) => {
    const res = await fetch(getApiEndpoint(`/api/paper-drafts/${selectedId}/advance`), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Advance failed');
    const updated = await res.json();
    setDrafts(prev => prev.map(d => d.id === selectedId ? updated : d));
  }, [selectedId]);

  const resetStage = useCallback(async () => {
    const res = await fetch(getApiEndpoint(`/api/paper-drafts/${selectedId}/reset`), {
      method: 'POST', headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Reset failed');
    const updated = await res.json();
    setDrafts(prev => prev.map(d => d.id === selectedId ? updated : d));
  }, [selectedId]);

  // Reset viewed stage when switching selected draft
  useEffect(() => { setViewStage(null); }, [selectedId]);

  const selectedDraft = drafts.find(d => d.id === selectedId);

  return (
    <div className="ga-page ph-no-capture">
      {/* Sidebar */}
      <div className={`ga-sidebar ${sidebarCollapsed ? 'ga-sidebar--collapsed' : ''}`}>
        <div className="ga-sidebar-header">
          {!sidebarCollapsed && <h2><FiBook style={{ verticalAlign: 'middle', marginRight: 6 }} />Papers</h2>}
          {!sidebarCollapsed && (
            <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={() => setShowCreate(true)}>
              <FiPlus /> New
            </button>
          )}
          <button className="ga-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Show list' : 'Hide list'}>
            {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        {loading ? (
          <div className="ga-sidebar-loading"><FiLoader className="ga-spin" /></div>
        ) : drafts.length === 0 ? (
          <div className="ga-sidebar-empty">
            <p>No paper drafts yet.</p>
            <button className="ga-btn ga-btn--outline" onClick={() => setShowCreate(true)}>
              <FiPlus /> Start a draft
            </button>
          </div>
        ) : (
          <ul className="ga-proposal-list">
            {drafts.map(d => (
              <li key={d.id}
                className={`ga-proposal-item${selectedId === d.id ? ' ga-proposal-item--active' : ''}`}
                onClick={() => setSelectedId(d.id)}>
                <div className="ga-proposal-item-body">
                  <span className="ga-proposal-title">{d.title}</span>
                  <span className="ga-proposal-stage">
                    {d.current_stage === 'done'
                      ? <><FiCheck style={{ color: '#22c55e', verticalAlign: 'middle' }} /> Done</>
                      : STAGES.find(s => s.key === d.current_stage)?.label || d.current_stage}
                    {d.stage_status === 'running' && <FiLoader className="ga-spin" style={{ marginLeft: 4 }} />}
                  </span>
                </div>
                <button className="ga-delete-btn" title="Delete"
                  onClick={e => { e.stopPropagation(); deleteDraft(d.id); }}>
                  <FiTrash2 />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Main panel */}
      <div className="ga-main">
        {!selectedDraft ? (
          <div className="ga-main-empty">
            <FiBook className="ga-empty-icon" size={40} />
            <h3>Papers</h3>
            <p>Select a draft from the list or create a new one from one of your collections.</p>
            <button className="ga-btn ga-btn--primary" onClick={() => setShowCreate(true)}>
              <FiPlus /> New Paper Draft
            </button>
            <label className="ga-btn ga-btn--outline" style={{ cursor: 'pointer', marginTop: 8 }}>
              <FiUpload /> Upload Published Paper (for style learning)
              <input type="file" accept=".pdf" hidden onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  const res = await fetch(getApiEndpoint('/api/paper-drafts/upload-published-paper'), {
                    method: 'POST', headers: getAuthHeaders(), body: formData,
                  });
                  if (res.ok) {
                    const data = await res.json();
                    alert(`Imported "${data.title}" — ${data.sections?.length || 0} sections. Style fingerprint will update.`);
                    window.location.reload();
                  } else {
                    const err = await res.json().catch(() => ({}));
                    alert(err.detail || 'Upload failed');
                  }
                } catch (err) { alert(err.message); }
                e.target.value = '';
              }} />
            </label>
          </div>
        ) : (
          <>
            <div className="ga-main-header">
              <h2>{selectedDraft.title}</h2>
            </div>
            <StageStepper draft={selectedDraft} viewStage={viewStage} onStageClick={setViewStage} />
            {showStageHints && selectedDraft && PA_STAGE_HINTS[selectedDraft.current_stage] && (
              <div className="pa-stage-hint">
                <span>{PA_STAGE_HINTS[selectedDraft.current_stage]}</span>
                <button onClick={() => { setShowStageHints(false); markGuideSeen(user?.id, 'stage_hints'); }}>&#10005; hide tips</button>
              </div>
            )}
            <div className="ga-stage-content">
              {(() => {
                // Post-done sub-pipeline (review_* keys + null landing on Results)
                if (selectedDraft.current_stage === 'done' && (viewStage == null || REVIEW_KEYS.includes(viewStage))) {
                  const reviewKey = REVIEW_KEYS.includes(viewStage) ? viewStage : 'review_results';
                  const tab = REVIEW_KEY_TO_TAB[reviewKey];
                  return (
                    <DoneStage
                      draft={selectedDraft}
                      collections={collections}
                      onRefresh={refreshDraft}
                      user={user}
                      tab={tab}
                      onTabChange={(t) => {
                        const found = Object.entries(REVIEW_KEY_TO_TAB).find(([, v]) => v === t);
                        setViewStage(found ? found[0] : 'review_results');
                      }}
                    />
                  );
                }

                // Active iteration loop: panel_review stage with a draft → DoneStage tabs
                if (selectedDraft.current_stage === 'panel_review' && selectedDraft.paper_sections) {
                  const stageToTab = { drafting: 'draft', panel_review: 'review' };
                  const tab = stageToTab[viewStage] || 'review';
                  return (
                    <DoneStage
                      draft={selectedDraft}
                      collections={collections}
                      onRefresh={refreshDraft}
                      user={user}
                      tab={tab}
                      onTabChange={(t) => {
                        const stageKey = Object.entries(stageToTab).find(([, v]) => v === t);
                        setViewStage(stageKey ? stageKey[0] : null);
                      }}
                      onAdvance={advanceStage}
                    />
                  );
                }

                const stage = viewStage || selectedDraft.current_stage;
                const isPast = !!viewStage && stageIndex(viewStage) < stageIndex(selectedDraft.current_stage);
                const allowEditAuthor = selectedDraft.current_stage === 'drafting' && selectedDraft.stage_status === 'error' && viewStage === 'author_profile';
                const readOnly = stage === 'author_profile' ? (isPast && !allowEditAuthor) : isPast;
                const draftForStage = isPast ? { ...selectedDraft, stage_status: 'done' } : selectedDraft;

                if (stage === 'collection_input') return (
                  <CollectionInputStage draft={draftForStage} collections={collections} onAdvance={advanceStage} readOnly={readOnly} />
                );
                if (stage === 'outline') return (
                  <OutlineStage draft={draftForStage} onAdvance={advanceStage} onReset={resetStage} readOnly={readOnly} />
                );
                if (stage === 'author_profile') return (
                  <AuthorProfileStage draft={draftForStage} onAdvance={advanceStage} readOnly={readOnly} />
                );
                if (stage === 'drafting') return (
                  <DraftingStage draft={draftForStage} onRefresh={refreshDraft} />
                );
                if (stage === 'done') return (
                  <DoneStage draft={draftForStage} collections={collections} onRefresh={refreshDraft} user={user} />
                );
                return null;
              })()}
            </div>
          </>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="ga-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <h3>New Paper Draft</h3>
            <div className="ga-form-group">
              <label>Source Collection</label>
              <select value={newForm.collection_id}
                onChange={e => setNewForm(p => ({ ...p, collection_id: e.target.value }))}>
                <option value="">Select collection…</option>
                {collections.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.item_count} papers)</option>
                ))}
              </select>
            </div>
            <div className="ga-form-group">
              <label>Paper Title</label>
              <input autoFocus value={newForm.title}
                onChange={e => setNewForm(p => ({ ...p, title: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && createDraft()}
                placeholder="e.g. A Survey of Transformer Architectures" />
            </div>
            <div className="ga-form-group">
              <label>Paper Type</label>
              <select value={newForm.paper_type}
                onChange={e => setNewForm(p => ({ ...p, paper_type: e.target.value }))}>
                {PAPER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="ga-modal-actions">
              <button className="ga-btn ga-btn--outline" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="ga-btn ga-btn--primary"
                disabled={!newForm.title.trim() || !newForm.collection_id || creating}
                onClick={createDraft}>
                {creating ? <><FiLoader className="ga-spin" /> Creating…</> : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
